import { useState, useMemo, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
  Stack,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { DataGrid } from "react-data-grid";
import { useDarkMode } from "../../common/contexts/GlobalContext";
import {
  getBulkTagSummaries,
  saveBulkTagSummaries,
} from "../../api/APIHelpers";
import { UniversalDescriptorContext } from "../../common/contexts/UniversalDescriptorContext";
import { AlertsContext } from "../../common/contexts/AlertsContext";
import { createBulkTagMappingSelectColumn } from "./BulkTagMappingSelectColumn";
import { getComparator } from "../helpers/getComparator";
import {
  getConfirmationTitle,
  getConfirmationContent,
} from "../helpers/confirmationHelpers";
import ConfirmDialog from "../../common/components/ConfirmDialog";
import BulkTagMappingSummaryStats from "./BulkTagMappingSummaryStats";
import BulkAssignmentPanel from "./BulkAssignmentPanel";
import BulkTagMappingFilters from "./BulkTagMappingFilters";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";
import "../../styles/BulkTagMappingLandingPage.css";
import BulkTagMappingResults from "./BulkTagMappingResults";

const BulkTagMappingLandingPage = () => {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAutocompleteRowId, setOpenAutocompleteRowId] = useState(null);
  const [tagNameFilter, setTagNameFilter] = useState("");
  const [assignedUDFilter, setAssignedUDFilter] = useState("");
  const [tenantFilter, setTenantFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [bulkAssignmentUD, setBulkAssignmentUD] = useState(null);
  const [pendingUDs, setPendingUDs] = useState(new Map()); // Map<rowId, UD>
  const [assigningRowId, setAssigningRowId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState(null);
  const [isApplyingBulkAssignment, setIsApplyingBulkAssignment] =
    useState(false);
  const [bulkAssignmentConfirmOpen, setBulkAssignmentConfirmOpen] =
    useState(false);
  const darkModeState = useDarkMode();
  const { universalDescriptors } = useContext(UniversalDescriptorContext);
  const { setInfoMessages } = useContext(AlertsContext);
  const [tenantIdToNameMap, setTenantIdToNameMap] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getBulkTagSummaries();
      const summaries = response.data?.TagNamesWithUDsAndCounts || [];

      if (!Array.isArray(summaries)) {
        setRows([]);
        return;
      }

      const transformedRows = summaries.map((summary, index) => {
        const ud = universalDescriptors.find(
          (descriptor) => descriptor.UniversalDescriptorId === summary.UDId
        );

        return {
          id: index + 1,
          tagName: summary.TagName || "",
          friendlyName: ud?.TagName || "",
          count: summary.Count || 0,
          assignedUniversalDescriptor: ud ? ud.UD : "",
          udId: summary.UDId,
          selectedUD: ud || null, // Store the UD object for Autocomplete
          tenantId: summary.TenantId,
          tenantDisplayName: summary.DisplayName || "",
        };
      });

      setRows(transformedRows);

      // Store a mapping of tenant IDs to names for easy lookup in filters and confirmation dialogs.
      const tenantMap = {};
      transformedRows.forEach((row) => {
        if (row.tenantId && row.tenantDisplayName) {
          tenantMap[row.tenantId] = row.tenantDisplayName;
        }
      });
      setTenantIdToNameMap(tenantMap);
    } catch (error) {
      setInfoMessages({
        type: "snackbar",
        message: `Error fetching bulk tag summaries: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [setInfoMessages, universalDescriptors]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getTenantName = useMemo(
    () => (tenantId) => {
      return tenantIdToNameMap[tenantId] || `TenantID ${tenantId}`;
    },
    [tenantIdToNameMap]
  );

  const filteredRows = useMemo(() => {
    let filtered = rows.filter((row) => {
      // Apply the Tag Name, Assigned UD, and Status filters first.
      if (
        tagNameFilter &&
        !row.tagName.toLowerCase().includes(tagNameFilter.toLowerCase())
      ) {
        return false;
      }

      if (assignedUDFilter) {
        const udName =
          row.selectedUD?.UD || row.assignedUniversalDescriptor || "";
        if (!udName.toLowerCase().includes(assignedUDFilter.toLowerCase())) {
          return false;
        }
      }
      if (statusFilter === "Assigned" && (row.udId == null || row.udId === 0)) {
        return false;
      }
      if (statusFilter === "Unassigned" && row.udId != null && row.udId !== 0) {
        return false;
      }

      // Apply Tenant filter only if a specific tenant is selected.
      if (tenantFilter !== "All" && row.tenantId !== parseInt(tenantFilter)) {
        return false;
      }

      return true;
    });

    const groupedMap = new Map();

    filtered.forEach((row) => {
      const key = `${row.tagName.toLowerCase()}|${row.udId || "null"}`;

      if (groupedMap.has(key)) {
        const existing = groupedMap.get(key);
        existing.count += row.count;
        // Track all raw row IDs that contribute to this aggregated row.
        if (!existing.sourceRowIds) {
          existing.sourceRowIds = [existing.id];
        }
        existing.sourceRowIds.push(row.id);
      } else {
        groupedMap.set(key, {
          ...row,
          tenantId: tenantFilter === "All" ? null : row.tenantId,
          // Keep the first row's data but the count is the sum of counts.
          count: row.count,
          // Track the source row IDs for this aggregated row.
          sourceRowIds: [row.id],
        });
      }
    });

    return Array.from(groupedMap.values());
  }, [rows, tagNameFilter, assignedUDFilter, tenantFilter, statusFilter]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === "ASC" ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [sortColumns, filteredRows]);

  // Calculate sum of tag counts for selected rows. This is displayed on the "Apply to X Tags" button.
  const selectedRowsCount = useMemo(() => {
    return Array.from(selectedRows)
      .map((rowId) => {
        const row = sortedRows.find((r) => r.id === rowId);
        return row ? row.count || 0 : 0;
      })
      .reduce((sum, count) => sum + count, 0);
  }, [selectedRows, sortedRows]);

  const summaryStats = useMemo(() => {
    const totalTags = filteredRows.reduce(
      (sum, row) => sum + (row.count || 0),
      0
    );
    const mappedTags = filteredRows
      .filter((row) => row.udId != null && row.udId !== 0)
      .reduce((sum, row) => sum + (row.count || 0), 0);
    const unmappedTags = totalTags - mappedTags;

    return { totalTags, mappedTags, unmappedTags };
  }, [filteredRows]);

  const handleAction = (row) => {
    if (row.udId) {
      handleUnmapClick(row);
    } else {
      handleAssignClick(row);
    }
  };

  const handleAssignClick = (row) => {
    const pendingUD = pendingUDs.get(row.id);
    if (!pendingUD) {
      setOpenAutocompleteRowId(row.id);
      return;
    }

    setConfirmDialogData({
      rowId: row.id,
      ud: pendingUD,
      count: row.count,
      tagName: row.tagName,
      tenantId: row.tenantId,
      isAssignment: true,
    });
    setConfirmDialogOpen(true);
  };

  const handleUnmapClick = (row) => {
    setConfirmDialogData({
      rowId: row.id,
      ud: row.selectedUD,
      count: row.count,
      tagName: row.tagName,
      tenantId: row.tenantId,
      isAssignment: false,
    });
    setConfirmDialogOpen(true);
  };

  const handleUDChange = (row, newUD) => {
    const newPendingUDs = new Map(pendingUDs);
    if (newUD) {
      newPendingUDs.set(row.id, newUD);
    } else {
      newPendingUDs.delete(row.id);
    }
    setPendingUDs(newPendingUDs);
    setOpenAutocompleteRowId(null); // Close Autocomplete after selection
  };

  // mappings: Array of { tagName: string, ud: object | null, tenantId: number | null, isAssignment: boolean }
  const saveBulkTagMappings = async (mappings) => {
    return await saveBulkTagSummaries(mappings);
  };

  // Does the work after the user chooses an action in the confirmation dialog.
  const handleConfirmAction = async (confirmed) => {
    if (!confirmed || !confirmDialogData) {
      setConfirmDialogOpen(false);
      setConfirmDialogData(null);
      return;
    }

    const { rowId, ud, tagName, tenantId, isAssignment } = confirmDialogData;
    setConfirmDialogOpen(false);
    setAssigningRowId(rowId);

    try {
      const mappings = [
        {
          TagName: tagName,
          UDId: ud.UniversalDescriptorId,
          TenantId: tenantId,
          IsAssignment: isAssignment,
        },
      ];

      const result = await saveBulkTagMappings(mappings);

      setInfoMessages({
        type: "dialog",
        message: <BulkTagMappingResults results={result.data} />,
        title: "Bulk Tag Mapping Result",
        open: true,
      });

      fetchData();

      if (isAssignment) {
        setPendingUDs((currentPendingUDs) => {
          const newPendingUDs = new Map(currentPendingUDs);
          newPendingUDs.delete(rowId);
          return newPendingUDs;
        });
      }
    } catch (error) {
      setInfoMessages({
        type: "snackbar",
        message: `Error ${confirmDialogData.isAssignment ? "assigning" : "unmapping"} Universal Descriptor: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
      });
    } finally {
      setAssigningRowId(null);
      setConfirmDialogData(null);
    }
  };

  const handleBulkAssignment = () => {
    if (!bulkAssignmentUD || selectedRows.size === 0) return;

    // Show confirmation dialog first
    setBulkAssignmentConfirmOpen(true);
  };

  const handleBulkAssignmentConfirm = async (confirmed) => {
    if (!confirmed) {
      setBulkAssignmentConfirmOpen(false);
      return;
    }
    if (!bulkAssignmentUD || selectedRows.size === 0) return;

    setBulkAssignmentConfirmOpen(false);
    setIsApplyingBulkAssignment(true);

    try {
      const selectedRowData = filteredRows.filter((row) =>
        selectedRows.has(row.id)
      );
      const mappings = selectedRowData.map((row) => ({
        TagName: row.tagName,
        UDId: bulkAssignmentUD.UniversalDescriptorId,
        TenantId: row.tenantId,
        IsAssignment: true,
      }));

      const result = await saveBulkTagMappings(mappings);

      setInfoMessages({
        type: "dialog",
        message: <BulkTagMappingResults results={result.data} />,
        title: "Bulk Tag Mapping Result",
        open: true,
      });

      fetchData();
    } catch (error) {
      setInfoMessages({
        type: "snackbar",
        message: `Error applying bulk assignment: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
      });
    } finally {
      setIsApplyingBulkAssignment(false);
    }
  };

  // Custom select column that conditionally shows checkbox based on UD assignment
  const customSelectColumn = useMemo(
    () =>
      createBulkTagMappingSelectColumn(
        selectedRows,
        setSelectedRows,
        sortedRows
      ),
    [selectedRows, sortedRows]
  );

  const columns = [
    customSelectColumn,
    {
      key: "tagName",
      name: "Tag Name",
      sortable: true,
    },
    {
      key: "count",
      name: "Count",
      sortable: true,
    },
    {
      key: "assignedUniversalDescriptor",
      name: "Assigned Universal Descriptor",
      sortable: true,
      renderCell: ({ row }) => {
        const activeUDs = universalDescriptors.filter((ud) => ud.IsActive);
        const hasAssignedUD = row.selectedUD != null;
        const pendingUD = pendingUDs.get(row.id);

        if (!hasAssignedUD) {
          // Show search box when no UD is assigned
          return (
            <Box sx={{ width: "100%", px: 1 }}>
              <Autocomplete
                size='small'
                options={activeUDs}
                getOptionLabel={(option) => option.UD}
                value={pendingUD || null}
                onOpen={() => setOpenAutocompleteRowId(row.id)}
                onClose={(event, reason) => {
                  // Close the dropdown if the user clicks outside it.
                  // Actual selection will be handled by onChange, which calls handleUDChange.
                  if (reason === "blur" || reason === "escape") {
                    setOpenAutocompleteRowId(null);
                  }
                }}
                onChange={(_, newValue) => {
                  if (newValue) {
                    handleUDChange(row, newValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Select Universal Descriptor ...'
                    variant='standard'
                    onClick={(e) => {
                      e.stopPropagation();
                      if (openAutocompleteRowId !== row.id) {
                        setOpenAutocompleteRowId(row.id);
                      }
                    }}
                    onFocus={() => {
                      if (openAutocompleteRowId !== row.id) {
                        setOpenAutocompleteRowId(row.id);
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "4px 8px",
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option?.UniversalDescriptorId === value?.UniversalDescriptorId
                }
                sx={{
                  width: "100%",
                  "& .MuiAutocomplete-inputRoot": {
                    padding: "0 !important",
                  },
                }}
                ListboxProps={{ style: { maxHeight: "200px" } }}
              />
            </Box>
          );
        }

        // Show UD when assigned
        return (
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{ width: "100%", px: 1 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "#017dba",
                color: "#fff",
                padding: "2px 8px",
                borderRadius: "12px",
                fontSize: "0.75rem",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}>
              <CheckCircle sx={{ fontSize: "14px" }} />
              <Typography variant='caption'>Assigned</Typography>
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
              {row.selectedUD.TagName || ""}
            </Typography>
            <Typography sx={{ fontSize: "0.875rem" }}>
              {row.selectedUD.UD || ""}
            </Typography>
          </Stack>
        );
      },
    },
    {
      key: "actions",
      name: "Actions",
      sortable: false,
      renderCell: ({ row }) => {
        const hasUD = row.udId != null && row.udId !== 0;
        const isAssigning = assigningRowId === row.id;
        const isAnyAssigning = assigningRowId != null; // Any row is being assigned

        if (isAssigning) {
          return (
            <Button
              size='small'
              variant='contained'
              disabled
              sx={{ minWidth: "120px" }}>
              UPDATING...
            </Button>
          );
        }

        return (
          <Button
            size='small'
            variant='contained'
            onClick={() => handleAction(row)}
            disabled={isAnyAssigning} // Disable if any assignment is in progress
            sx={
              hasUD
                ? {
                    // Dark grey
                    backgroundColor: "#616161",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#424242",
                    },
                  }
                : {} //Primary blue from theme, nothing to do
            }>
            {hasUD ? "Unmap" : "Assign"}
          </Button>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
      }}>
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant='h5' gutterBottom>
          Bulk Tag Mapping
        </Typography>
        <Typography variant='h10' gutterBottom>
          Select tags, choose a Universal Descriptor, then apply. Supports bulk
          operations on thousands of tags.
        </Typography>
        <BulkTagMappingSummaryStats summaryStats={summaryStats} />

        <BulkAssignmentPanel
          universalDescriptors={universalDescriptors}
          bulkAssignmentUD={bulkAssignmentUD}
          onUDChange={(_, newValue) => setBulkAssignmentUD(newValue)}
          selectedRowsCount={selectedRowsCount}
          selectedRowsSize={selectedRows.size}
          isApplying={isApplyingBulkAssignment}
          onApply={handleBulkAssignment}
        />

        <BulkTagMappingFilters
          tagNameFilter={tagNameFilter}
          onTagNameFilterChange={setTagNameFilter}
          assignedUDFilter={assignedUDFilter}
          onAssignedUDFilterChange={setAssignedUDFilter}
          tenantFilter={tenantFilter}
          onTenantFilterChange={setTenantFilter}
          rows={rows}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}>
        <link
          rel='stylesheet'
          type='text/css'
          href={
            darkModeState ? "css/DataGridDark.css" : "css/DataGridLight.css"
          }
        />
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <DataGrid
            columns={columns}
            rows={sortedRows}
            className={darkModeState ? "rdg-dark" : "rdg-light"}
            defaultColumnOptions={{
              resizable: true,
            }}
            selectedRows={selectedRows}
            onSelectedRowsChange={setSelectedRows}
            rowKeyGetter={(row) => row.id}
            sortColumns={sortColumns}
            onSortColumnsChange={setSortColumns}
            style={{ height: "100%" }}
          />
        </Box>
      </Box>

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleConfirmAction}
        title={getConfirmationTitle(confirmDialogData)}
        content={getConfirmationContent(confirmDialogData, getTenantName)}
      />

      {/* Bulk Assignment Confirmation Dialog */}
      <ConfirmDialog
        open={bulkAssignmentConfirmOpen}
        onClose={handleBulkAssignmentConfirm}
        title='Confirm Assignment'
        content={
          <Typography>
            Do you want to assign the Universal Descriptor{" "}
            <strong>{bulkAssignmentUD?.UD || ""}</strong> to{" "}
            <strong>{selectedRowsCount.toLocaleString()}</strong> tags?
            <br></br>
            This change will apply to{" "}
            <strong>
              {tenantFilter === "All"
                ? "all tenants"
                : `the ${getTenantName(tenantFilter)} Tenant`}
            </strong>
            .
          </Typography>
        }
      />
    </Box>
  );
};

export default BulkTagMappingLandingPage;
